import {
  faArrowRight,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons'
import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@radix-ui/react-navigation-menu'
import clsx from 'clsx'
import { useTranslations } from 'next-intl'
import Link from 'next/link'
import { cn } from 'utils/cn'
import { MobileFooter } from './MobileFooter'
import { MobileQuickAccessMenu } from './MobileQuickAccessMenu'
import { type Menu } from './typings'
import { getDataByLocale, isMenu } from './utils'

export const MobileCategoryMenu = ({ locale }: { locale: string }) => {
  const t = useTranslations('Navigation.MobileCategoryMenu')
  const data = getDataByLocale(locale)
  const items = [...data.main, ...data.category]

  return (
    <NavigationMenuList
      className='bg-surface-primary text-body-md font-manrope z-50 overflow-y-scroll'
      data-testid='mobile-category-menu'
    >
      <div className='px-4 py-6 font-bold'>{t('headline')}</div>
      <div className='flex flex-col'>
        {items.map((item, i) => (
          <NavigationMenuItem
            className='px-4 font-normal'
            key={i}
            value={item.title}
          >
            {isMenu(item) ? (
              <>
                <NavigationMenuTrigger
                  // prevent open on hover
                  className={cn(
                    'hover:text-primary-700 active:text-primary-800 flex w-full items-center justify-between gap-2 p-3 text-start',
                    { 'text-primary-700 font-bold': item.active },
                  )}
                  onMouseLeave={(e) => {
                    e.preventDefault()
                  }}
                  onPointerMove={(e) => {
                    e.preventDefault()
                  }}
                  onPointerDown={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                  }}
                >
                  {item.title}
                  <FontAwesomeIcon icon={faChevronRight} />
                </NavigationMenuTrigger>
                <NavigationMenuContent
                  className={cn(
                    'data-[state=open]:animate-enterFromRight bg-surface-primary data-[motion=from-end]:animate-enterFromRight data-[motion=to-start]:animate-exitToRight data-[motion=to-end]:animate-exitToRight absolute left-0 top-0 z-50 flex min-h-full w-full flex-col data-[state=closed]:-z-10 data-[state=closed]:opacity-0 data-[state=open]:opacity-100',
                  )}
                  forceMount
                >
                  <CategorySubMenu menu={item} />
                </NavigationMenuContent>
              </>
            ) : (
              <NavigationMenuLink
                asChild
                className={cn(
                  'hover:text-primary-700 active:text-primary-800 flex gap-2 p-3',
                  { 'text-primary-700 font-bold': item.active },
                )}
                onPointerDown={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                }}
              >
                <Link href={item.path} prefetch={false}>
                  {item.title}
                </Link>
              </NavigationMenuLink>
            )}
          </NavigationMenuItem>
        ))}
        <div className='bg-surface-tealgrey-50'>
          <MobileQuickAccessMenu locale={locale} />
          <MobileFooter />
        </div>
      </div>
    </NavigationMenuList>
  )
}

const CategorySubMenu = ({ menu }: { menu: Menu }) => {
  const t = useTranslations('Navigation.MobileCategoryMenu')

  return (
    <div
      className={cn('bg-surface-primary text-body-md font-manrope z-50')}
      data-testid='mobile-category-submenu'
    >
      <NavigationMenuList className='flex flex-col gap-2 pb-4'>
        <NavigationMenuItem className='bg-surface-tealgrey-50 mb-2 flex w-full'>
          <NavigationMenuTrigger
            // prevent open on hover
            aria-label={t('back')}
            className={cn('flex items-center gap-2 p-4 font-bold')}
            onMouseLeave={(e) => {
              e.preventDefault()
            }}
            onPointerMove={(e) => {
              e.preventDefault()
            }}
            onPointerDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
            <span>{menu.title}</span>
          </NavigationMenuTrigger>
        </NavigationMenuItem>
        {menu.children.map(({ path, title, active, subTitle }, j) => (
          <NavigationMenuItem className='group' key={j}>
            <NavigationMenuLink
              asChild
              className={cn(
                'hover:text-primary-700 active:text-primary-800 flex w-full flex-col px-7 py-3',
                {
                  'text-primary-700 font-bold': active,
                },
              )}
              onPointerDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
            >
              <Link href={path} prefetch={false}>
                {title}
                {subTitle ? (
                  <span
                    className={clsx(
                      'text-body-sm group-hover:text-primary-700 group-active:text-primary-800',
                      {
                        'text-primary-700': active,
                        'text-text-400': !active,
                      },
                    )}
                  >
                    {subTitle}
                  </span>
                ) : null}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>
        ))}
        {menu.more ? (
          <NavigationMenuLink asChild>
            <Link
              className={cn(
                'text-body-md text-primary-700 active:text-primary-800 flex w-full items-center gap-2 p-3 px-7 py-3 hover:underline',
                { 'font-bold': menu.more.active },
              )}
              href={menu.more.path}
              onPointerDown={(e) => {
                e.preventDefault()
                e.stopPropagation()
              }}
              prefetch={false}
            >
              {menu.more.title}
              <FontAwesomeIcon icon={faArrowRight} />
            </Link>
          </NavigationMenuLink>
        ) : null}
      </NavigationMenuList>
    </div>
  )
}
